import Relay from 'react-relay';
import { fromGlobalId } from 'graphql-relay';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

function normalizeSnapshotEntityId(id?: string | null): string | null {
    if (!id) {
        return null;
    }

    if (uuidRegex.test(id)) {
        return id;
    }

    try {
        const decodedId = fromGlobalId(id);
        return decodedId.id || id;
    } catch (e) {
        return id;
    }
}

function getPreferredSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null): string | null {
    if (!entity) {
        return null;
    }

    return entity.realId || normalizeSnapshotEntityId(entity.id);
}

export interface AcceptSnapshotMutationProps {
    testInvocation: {
        id: string;
        realId?: string;
        version: number;
        expectedOutput?: any;
        runs?: {
            edges: Array<{
                node: {
                    id?: string;
                    realId?: string;
                    actualOutput?: any;
                    computedDiff?: any;
                };
            }>;
        };
    };
    testRunId?: string;
}

export interface AcceptSnapshotMutationResponse {
    testInvocation: {
        id: string;
        version: number;
        expectedOutput?: any;
    } | null;
    success: boolean;
    conflict: boolean;
    error: string | null;
}

export default class AcceptSnapshotMutation extends Relay.Mutation<AcceptSnapshotMutationProps, AcceptSnapshotMutationResponse> {
    private mutationProps: AcceptSnapshotMutationProps;

    constructor(props: AcceptSnapshotMutationProps) {
        super(props as any);
        this.mutationProps = props;
    }

    getMutation() {
        return Relay.QL`mutation {acceptTestInvocationSnapshot}`;
    }

    getFatQuery() {
        return Relay.QL`
            fragment on AcceptSnapshotPayload {
                testInvocation {
                    id
                    expectedOutput
                    version
                }
                success
                conflict
                error
            }
        `;
    }

    getConfigs() {
        return [{
            type: 'FIELDS_CHANGE',
            fieldIDs: {
                testInvocation: this.mutationProps.testInvocation.id,
            },
        }, {
            // FIELDS_CHANGE only selects tracked entity fields; the callbacks also need the
            // acknowledgement fields, which are never tracked in the store.
            type: 'REQUIRED_CHILDREN',
            children: [Relay.QL`
                fragment on AcceptSnapshotPayload {
                    success
                    conflict
                    error
                }
            `],
        }];
    }

    getVariables() {
        const testInvocationId = getPreferredSnapshotEntityId(this.mutationProps.testInvocation) || this.mutationProps.testInvocation.id;
        const variables: {
            testInvocationId: string;
            version: number;
            testRunId?: string;
        } = {
            testInvocationId,
            version: this.mutationProps.testInvocation.version,
        };

        if (this.mutationProps.testRunId) {
            variables.testRunId = this.mutationProps.testRunId;
        }

        return variables;
    }

    getOptimisticResponse() {
        const { testInvocation, testRunId } = this.mutationProps;
        const runs = testInvocation.runs ? testInvocation.runs.edges.map(e => e.node) : [];
        const lastRun = testRunId
            ? runs.find(run => run && getPreferredSnapshotEntityId(run) === testRunId) || runs[0]
            : runs.length > 0 ? runs[0] : null;

        let newExpectedOutput = testInvocation.expectedOutput;
        
        if (lastRun) {
            if (lastRun.actualOutput) {
                newExpectedOutput = lastRun.actualOutput;
            }
        }

        return {
            testInvocation: {
                id: testInvocation.id,
                version: testInvocation.version + 1,
                expectedOutput: newExpectedOutput,
            },
            success: true,
            conflict: false,
            error: null,
        };
    }
}
